import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const AuthContext = createContext();
const ADMIN_TOKEN_KEY = 'adminToken';

const applyAdminToken = (token) => {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isRegistrationCompleted, setIsRegistrationCompleted] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isRegistrationLoading, setIsRegistrationLoading] = useState(true);

  // Load registration status from database on initial render
  useEffect(() => {
    axios.get('/api/registration-status')
      .then(response => {
        if (response.data) {
          setIsRegistrationCompleted(response.data.isCompleted);
        }
      })
      .catch(error => {
        console.error('Error loading registration status:', error);
        // Fallback to localStorage if API fails
        const savedState = localStorage.getItem('isRegistrationCompleted');
        if (savedState === 'true') {
          setIsRegistrationCompleted(true);
        }
      })
      .finally(() => {
        setIsRegistrationLoading(false);
      });
  }, []);

  // Restore and verify the short-lived admin token for this browser tab.
  useEffect(() => {
    const token = sessionStorage.getItem(ADMIN_TOKEN_KEY);

    if (!token) {
      setIsAuthLoading(false);
      return;
    }

    applyAdminToken(token);
    axios.get('/api/admin/check')
      .then(() => {
        setIsAuthenticated(true);
      })
      .catch(() => {
        sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        applyAdminToken(null);
      })
      .finally(() => {
        setIsAuthLoading(false);
      });
  }, []);

  // Save to localStorage as a backup
  useEffect(() => {
    localStorage.setItem('isRegistrationCompleted', isRegistrationCompleted);
  }, [isRegistrationCompleted]);

  const login = useCallback(async (password) => {
    const response = await axios.post('/api/admin/login', { password });
    const { token } = response.data;

    sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
    applyAdminToken(token);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post('/api/admin/logout');
    } finally {
      sessionStorage.removeItem(ADMIN_TOKEN_KEY);
      applyAdminToken(null);
      setIsAuthenticated(false);
    }
  }, []);

  // The context is the single place that persists registration status.
  const updateRegistrationStatus = useCallback(async (status) => {
    await axios.post('/api/registration-status', { isCompleted: status });
    setIsRegistrationCompleted(status);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        isRegistrationCompleted,
        setIsRegistrationCompleted: updateRegistrationStatus,
        isLoading: isAuthLoading || isRegistrationLoading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};